import React, { useRef, useContext } from "react";
import { BetQLSportItem } from "src/common-services";
import { StickyContainer } from "react-sticky";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DroppableProvided,
  DraggableProvided,
  DraggableStateSnapshot,
  DropResult,
} from "react-beautiful-dnd";
import Responsive from "react-responsive";
import styles from "./styles.scss";
import GameMobileRow from "./GameMobileRow";
import { getHeaders, getColumns } from "./ColumnService";
import { TrackingProperties } from "../../constants";
import { SubscriptionContext } from "../../services/SubscriptionService";
import GamesTableFreeTrialBanner from "./FreeTrialBanner";

export interface GamesTableProps {
  style?: any;
  games: any[];
  filterKey: string;
  pageKey: string;
  currentSportObj: BetQLSportItem;
  trackingProps: TrackingProperties;
  onDragEnd(result: DropResult): void;
  selectedSortColumnId: number;
  columnSortDesc: boolean;
  onSelectSortColumn(columnId: number): void;
  columnOrder: number[];
  adEvents: {
    valueEvents: number;
    sharpEvents: number;
    message: string;
  };
  pageValue: string;
}

const GamesTable = ({
  games,
  filterKey,
  pageKey,
  currentSportObj,
  trackingProps,
  selectedSortColumnId,
  columnSortDesc,
  onSelectSortColumn,
  columnOrder,
  adEvents,
  onDragEnd,
}: GamesTableProps) => {
  const { hasSubscriptionAccess } = useContext(SubscriptionContext);
  const hasAccess = hasSubscriptionAccess(currentSportObj.key);
  const gamesTableRef = useRef<any>();
  const showAd = games.length && !hasAccess;
  const adIndex = Math.max(1, games.filter((x) => x.freeGame).length);

  const ads = !showAd
    ? []
    : [
        {
          index: adIndex,
          component: () => {
            return (
              <GamesTableFreeTrialBanner
                key={`banner-${adIndex}`}
                title={`Subscribe Now for BetQL’s ${currentSportObj.name} Plays`}
                adEvents={adEvents}
              />
            );
          },
        },
      ];

  const periodFilter =
    filterKey === "spread" || filterKey === "moneyline" || filterKey === "total" ? `FG${filterKey}` : filterKey;

  // iterate through each game and add its columns JSX into its corresponding
  // column array
  const columnHeaders = getHeaders(
    filterKey,
    selectedSortColumnId,
    onSelectSortColumn,
    columnSortDesc,
    currentSportObj.url,
    pageKey
  );

  const gamesColumns = getColumns(
    columnHeaders,
    games,
    filterKey,
    pageKey,
    currentSportObj,
    trackingProps,
    ads,
    gamesTableRef
  );

  return (
    <React.Fragment>
      {/* We are only using the Responsive component here because
      the games table is clientside render only. These componenets should never be used
      on something we need to rendered server side since it won't know the screen width */}
      <Responsive minWidth={768}>
        <div ref={gamesTableRef} className="games-container">
          <div key={0} className="games-table-column">
            <StickyContainer>{gamesColumns[0]}</StickyContainer>
          </div>
          <StickyContainer className="sticky-container">
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="table" type="COLUMN" direction="horizontal">
                {(dropProvided: DroppableProvided) => {
                  // build the rows in the table based on the set order
                  const columns: any[] = [];

                  columnOrder.forEach((orderIndex: number, index: number) => {
                    if (orderIndex !== 0) {
                      const rows: any[] = gamesColumns[orderIndex];
                      columns.push(
                        <Draggable key={orderIndex} draggableId={`${orderIndex}`} index={index}>
                          {(dragProvided: DraggableProvided, dragSnapshot: DraggableStateSnapshot) => {
                            const draggingStyle = dragSnapshot.isDragging
                              ? {
                                  borderLeft: "1px solid #ebebeb",
                                  zIndex: 0,
                                }
                              : {};
                            let style = {
                              ...dragProvided.draggableProps.style,
                              ...draggingStyle,
                            };
                            return (
                              <div
                                className="games-table-column"
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                {...dragProvided.dragHandleProps}
                                style={style}
                              >
                                {rows}
                              </div>
                            );
                          }}
                        </Draggable>
                      );
                    }
                  });
                  return (
                    <div ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                      <div
                        className="games-table"
                        style={{
                          gridTemplateColumns: `repeat(${columnOrder.length}, 1fr)`,
                        }}
                      >
                        {columns}
                        {dropProvided.placeholder}
                      </div>
                    </div>
                  );
                }}
              </Droppable>
            </DragDropContext>
          </StickyContainer>
        </div>
      </Responsive>
      {/* We are only using the Responsive component here because
        the games table is clientside render only. These componenets should never be used
        on something we need to rendered server side since it won't know the screen width */}
      <Responsive maxWidth={767}>
        <div className="mobile-games-container">
          {games.map((game: any, index: number) => (
            <div key={`mobile-table-row-${index}`}>
              {!hasAccess && adIndex === index && (
                <GamesTableFreeTrialBanner
                  title={`Get Access To Our ${currentSportObj.name} Picks`}
                  adEvents={adEvents}
                />
              )}

              <GameMobileRow
                columnOrder={columnOrder}
                currentSportObj={currentSportObj}
                game={game}
                filterKey={filterKey}
                pageKey={pageKey}
                periodFilter={periodFilter}
                trackingProps={trackingProps}
                open={false} // index === 0
                drawerIndex={undefined} // index === 0 ? 0 : undefined
              />
            </div>
          ))}
        </div>
      </Responsive>
      <style jsx global>
        {styles}
      </style>
    </React.Fragment>
  );
};

export default GamesTable;
